import { memo, useState } from "react";
import {
  FloatingPortal,
  autoUpdate,
  offset,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { Handle, Position } from "@xyflow/react";
import { Outcome } from "@unlikelyai-magic/shared/data-access/api";
import { Paragraph } from "@unlikelyai-magic/ui/typography";
import { cn } from "@unlikelyai-magic/utils";
import { UpdateType, type UpdatedNodeInfo } from "../types";

interface EditedNodeData {
  label: string;
  value: Outcome;
  question: string;
  editType: EditType;
  updateInfo: UpdatedNodeInfo;
  reasoning?: string;
}

type EditType = "EDIT" | "ADD" | "DELETE";

export interface EditedConditionNodeProps {
  id: string;
  data: EditedNodeData;
  isConnectable: boolean;
}

export const EditedConditionNode = memo(
  ({ id, data, isConnectable }: EditedConditionNodeProps) => {
    const [open, setOpen] = useState(false);
    const { refs, floatingStyles, context } = useFloating({
      open,
      onOpenChange: setOpen,
      placement: "top",
      middleware: [offset(8)],
      whileElementsMounted: autoUpdate,
    });
    const hover = useHover(context, { move: false, restMs: 50 });
    const role = useRole(context, { role: "tooltip" });
    const { getReferenceProps, getFloatingProps } = useInteractions([
      hover,
      role,
    ]);

    const { updateInfo } = data;
    const editType = data.editType;

    const renderContent = () => {
      if (editType == "DELETE") {
        return (
          <div className="text-gray-500 line-through">
            <Paragraph className="font-semibold text-gray-500">
              {data.question}
            </Paragraph>
          </div>
        );
      }

      if (editType == "EDIT") {
        return (
          <div className="flex flex-col gap-1">
            <div className="line-through text-gray-400 text-sm">
              {data.question}
            </div>
            <Paragraph className="font-semibold text-green-700">
              {updateInfo.rephrased}
            </Paragraph>
          </div>
        );
      }

      if (editType == "ADD") {
        return (
          <div className="flex flex-col gap-1">
            <Paragraph className="font-semibold">
              {data.question ?? data.label}
            </Paragraph>
          </div>
        );
      }

      return (
        <Paragraph className="font-semibold">{data.question}</Paragraph>
      );
    };

    const getNodeStyle = () => {
      if (editType == "DELETE") {
        return "bg-gray-200 border-gray-400";
      }
      if (editType == "EDIT") {
        return "bg-yellow-50 border-yellow-400";
      }
      if (editType == "ADD") {
        return "bg-green-50 border-green-400";
      }
      return "bg-white border-gray-300";
    };

    return (
      <>
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={isConnectable}
          className="opacity-0"
        />
        <div
          ref={refs.setReference}
          {...getReferenceProps()}
          className={cn(
            "px-4 py-3 rounded-lg border-2 min-w-[200px] max-w-[400px]",
            "transition-all duration-200",
            getNodeStyle()
          )}
        >
          <div className="flex items-center gap-2 mb-1">
            <span
              className={cn(
                "text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded",
                editType == "DELETE" && "bg-gray-300 text-gray-700",
                editType == "EDIT" && "bg-yellow-200 text-yellow-800",
                editType == "ADD" && "bg-green-200 text-green-800"
              )}
            >
              {editType}
            </span>
          </div>
          {renderContent()}
        </div>
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={isConnectable}
          className="opacity-0"
        />

        {open && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className="max-w-md rounded-lg p-3 bg-white border border-gray-200 shadow-lg z-[9999]"
            >
              <div className="text-sm text-gray-700 mb-2">
                {updateInfo.explanation}
              </div>
              {updateInfo.note && (
                <div className="text-sm text-blue-600 italic">
                  Note: {updateInfo.note}
                </div>
              )}
            </div>
          </FloatingPortal>
        )}
      </>
    );
  }
);

EditedConditionNode.displayName = "EditedConditionNode";
